"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import useSWR, { useSWRConfig } from "swr";
import { API_BASE_URL } from "@/lib/config";
import { NotebookCell } from "../../notebook/NotebookCell";
import { motion } from "framer-motion";
import { Code2, CircleCheckBig, Maximize2, LayoutDashboard } from "lucide-react";
import { useArtifact } from "@/hooks/use-artifact";
import { getViewModeChrome } from "../view-mode-chrome";
import { isNoiseStdoutText } from "@/lib/alert-output-detection";

interface PlotlyData {
  data: Array<any>;
  layout?: any;
  config?: any;
}

interface AddCellSuccessProps {
  stepIndex?: number;
  type?: "add" | "replace" | "edit";
  sessionId?: string;
  cellInfo?: {
    id?: string;
    content: string;
    index?: number;
    purpose?: string;
    outputs?: Array<{
      name?: string;
      output_type: "stream" | "display_data" | "execute_result" | "error";
      text?: string;
      data?: any;
    }>;
  };
  result?: {
    execution_result?: any;
    status?: string;
    error_type?: string;
    error_message?: string;
    iteration?: number;
    stage?: string;
    timestamp?: number;
    id?: string;
  };
  hideContent?: boolean;
  blurContent?: boolean;
  fallbackOutputs?: Array<any>;
  triggerRefresh?: boolean;
  viewMode?: string;
}

const notebookFetcher = async (url: string) => {
  const response = await fetch(url, { credentials: "include" });

  if (!response.ok) {
    throw new Error(`Failed to fetch notebook data: ${response.status}`);
  }

  return response.json();
};

// Transform Jupyter notebook output format to NotebookCell format
export function transformOutputs(
  jupyterOutputs: Array<{
    name?: string;
    output_type: "stream" | "display_data" | "execute_result" | "error";
    text?: string;
    data?: any;
  }>
): Array<{
  type: "text" | "image" | "error" | "html" | "plotly" | "unified_search" | "signal_register" | "automations" | "bokeh" | "fund_agent";
  data: string | PlotlyData | any;
}> {
  const transformed: Array<{
    type: "text" | "image" | "error" | "html" | "plotly" | "unified_search" | "signal_register" | "automations" | "bokeh" | "fund_agent";
    data: string | PlotlyData | any;
  }> = [];

  for (const output of jupyterOutputs) {
    // Handle pre-transformed outputs (PKA sometimes returns {type, data} instead of {output_type, data})
    const rawOutput = output as any;
    if (rawOutput.type && !rawOutput.output_type) {
      // Already in NotebookCell format — pass through directly
      const validTypes = ["text", "image", "error", "html", "plotly", "unified_search", "signal_register", "automations", "bokeh", "fund_agent", "sub_agent", "dashboard", "backtest-tabs"];
      if (validTypes.includes(rawOutput.type)) {
        transformed.push({ type: rawOutput.type, data: rawOutput.data });
      }
      continue;
    }

    if (output.output_type === "stream" && output.text) {
      transformed.push({
        type: "text",
        data: output.text,
      });
    } else if (output.output_type === "error" && output.text) {
      transformed.push({
        type: "error",
        data: output.text,
      });
    } else if (
      (output.output_type === "display_data" ||
        output.output_type === "execute_result") &&
      output.data
    ) {
      const data = output.data;

      // Check for Plotly data
      if (data["application/vnd.plotly.v1+json"]) {
        transformed.push({
          type: "plotly",
          data: data["application/vnd.plotly.v1+json"],
        });
      }

      // Check for unified search data
      if (data["application/vnd.xynth.unified-search+json"]) {
        transformed.push({
          type: "unified_search",
          data: data["application/vnd.xynth.unified-search+json"],
        });
      }

      // Check for signal registration data
      if (data["application/vnd.xynth.signal-register+json"]) {
        transformed.push({
          type: "signal_register",
          data: data["application/vnd.xynth.signal-register+json"],
        });
      }

      // Check for automations data
      if (data["application/vnd.xynth.automations+json"]) {
        transformed.push({
          type: "automations",
          data: data["application/vnd.xynth.automations+json"],
        });
      }

      // Check for fund agent data
      if (data["application/vnd.xynth.fund-agent+json"]) {
        transformed.push({
          type: "fund_agent",
          data: data["application/vnd.xynth.fund-agent+json"],
        });
      }

      // Check for HTML visualizations (dashboard, backtest-tabs, or Bokeh)
      const rawHtml = data["text/html"] as string | undefined;
      if (rawHtml) {
        const normalized = rawHtml.replace(/\s+/g, " ").trim().toLowerCase();
        const isDashboardHtml =
          normalized.includes('data-xynth-component="dashboard"');
        const isBacktestTabsHtml =
          normalized.includes("data-visualization-type=\"backtest-tabs\"") &&
          normalized.includes("data-xynth-component=\"true\"");
        const isBokehHtml =
          normalized.includes("bokeh") &&
          (normalized.includes("<script") || normalized.includes("bk-root"));

        if (isDashboardHtml) {
          // Extract inner HTML from the dashboard wrapper div
          const innerMatch = rawHtml.match(/<div\s+data-xynth-component="dashboard">([\s\S]*)<\/div>\s*$/i);
          const dashboardHtml = innerMatch ? innerMatch[1] : rawHtml;
          transformed.push({
            type: "dashboard" as any,
            data: dashboardHtml,
          });
        } else if (isBacktestTabsHtml) {
          // Extract base64 data-payload, decode, and parse as BacktestPayload
          const payloadMatch = rawHtml.match(/data-payload="([^"]+)"/);
          if (payloadMatch) {
            try {
              const decoded = atob(payloadMatch[1]);
              const parsed = JSON.parse(decoded);
              transformed.push({
                type: "backtest-tabs" as any,
                data: parsed,
              });
            } catch {
              // Fallback: push raw HTML if decoding fails
              transformed.push({ type: "html", data: rawHtml });
            }
          } else {
            transformed.push({ type: "html", data: rawHtml });
          }
        } else if (isBokehHtml) {
          transformed.push({
            type: "bokeh",
            data: rawHtml,
          });
        } else if (data["text/plain"] && !isNoiseStdoutText(data["text/plain"])) {
          // Fallback: treat non-whitelisted HTML as plain text if available.
          // Skip IPython object reprs / "[Plotly Figure]" noise so a dashboard
          // cell never previews as "<IPython.core.display.HTML object>".
          transformed.push({
            type: "text",
            data: data["text/plain"],
          });
        }
      }

      // Check for images
      if (data["image/png"]) {
        transformed.push({
          type: "image",
          data: data["image/png"],
        });
      } else if (data["image/jpeg"]) {
        transformed.push({
          type: "image",
          data: data["image/jpeg"],
        });
      } else if (data["image/svg+xml"]) {
        transformed.push({
          type: "image",
          data: data["image/svg+xml"],
        });
      }

      // Check for plain text (fallback) when no HTML or images handled above
      if (
        data["text/plain"] &&
        !isNoiseStdoutText(data["text/plain"]) &&
        !rawHtml &&
        !data["image/png"] &&
        !data["image/jpeg"] &&
        !data["application/vnd.plotly.v1+json"] &&
        !data["application/vnd.xynth.unified-search+json"] &&
        !data["application/vnd.xynth.signal-register+json"] &&
        !data["application/vnd.xynth.automations+json"] &&
        !data["application/vnd.xynth.fund-agent+json"]
      ) {
        transformed.push({
          type: "text",
          data: data["text/plain"],
        });
      }
    }
  }

  return transformed;
}

export function AddCellSuccess({
  stepIndex = 1,
  cellInfo,
  type = "add",
  sessionId,
  result,
  hideContent = false,
  blurContent = false,
  fallbackOutputs,
  triggerRefresh = false,
  viewMode,
}: AddCellSuccessProps) {
  const chrome = getViewModeChrome(viewMode);
  const { mutate } = useSWRConfig();
  const pathname = usePathname();
  const didRunRef = useRef(false);
  const hydrationLogRef = useRef<string | null>(null);
  const isReplay = pathname?.startsWith("/replay/");
  const notebookUrl = sessionId
    ? isReplay
      ? `${API_BASE_URL}/replay/notebook/${sessionId}`
      : `${API_BASE_URL}/strategies/active-run-details/${sessionId}`
    : null;
  const { data: notebookData, error: notebookError } = useSWR(
    notebookUrl,
    notebookFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Trigger mutation when component renders to refresh active run data
  useEffect(() => {
    if (!triggerRefresh || !notebookUrl) return;
    if (didRunRef.current) return;
    didRunRef.current = true;
    console.info("[InlineHydration] refreshing notebook cache", {
      sessionId,
      notebookUrl,
      cellId: cellInfo?.id ?? null,
      cellIndex: cellInfo?.index ?? null,
    });
    mutate(notebookUrl);
    const followUpRefreshes = [800, 1800].map((delay) =>
      setTimeout(() => {
        mutate(notebookUrl);
      }, delay)
    );

    return () => {
      followUpRefreshes.forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, [triggerRefresh, notebookUrl, mutate, sessionId, cellInfo?.id, cellInfo?.index]);

  // Check if this is a canceled state
  const isCanceled = result?.status === "canceled";

  // Transform outputs to NotebookCell format (always compute for hooks rules)
  // Use cell_info.outputs first, fall back to execution_result.outputs if empty
  const rawOutputs = useMemo(() => {
    if (cellInfo?.outputs && cellInfo.outputs.length > 0) {
      return cellInfo.outputs;
    }

    if (fallbackOutputs && fallbackOutputs.length > 0) {
      return fallbackOutputs;
    }

    return [];
  }, [cellInfo?.outputs, fallbackOutputs]);
  const sessionOutputs = useMemo(() => {
    if (!rawOutputs || rawOutputs.length === 0) {
      return [];
    }
    return transformOutputs(rawOutputs);
  }, [rawOutputs]);
  const matchedNotebookCell = useMemo(() => {
    const cells = notebookData?.cells;
    if (!Array.isArray(cells) || !cellInfo) {
      return null;
    }

    const sessionCellId = cellInfo.id ? String(cellInfo.id) : null;
    if (sessionCellId) {
      const idMatch = cells.find((cell: any) => String(cell?.id) === sessionCellId);
      if (idMatch) {
        return { cell: idMatch, matchedBy: "id" as const };
      }
    }

    if (typeof cellInfo.index === "number") {
      const indexMatch = cells.find((cell: any) => cell?.index === cellInfo.index);
      if (indexMatch) {
        return { cell: indexMatch, matchedBy: "index" as const };
      }
    }

    return null;
  }, [notebookData?.cells, cellInfo]);
  const notebookOutputs = useMemo(() => {
    const outputs = matchedNotebookCell?.cell?.outputs;
    if (!Array.isArray(outputs) || outputs.length === 0) {
      return [];
    }
    return transformOutputs(outputs as any);
  }, [matchedNotebookCell]);
  const transformedOutputs =
    notebookOutputs.length > 0 ? notebookOutputs : sessionOutputs;

  useEffect(() => {
    if (hideContent || !cellInfo || !sessionId) {
      return;
    }

    if (notebookError) {
      const signature = `error:${cellInfo.id ?? "none"}:${cellInfo.index ?? "none"}:${String(notebookError)}`;
      if (hydrationLogRef.current === signature) {
        return;
      }
      hydrationLogRef.current = signature;
      console.error("[InlineHydration] notebook fetch failed", {
        sessionId,
        notebookUrl,
        cellId: cellInfo.id ?? null,
        cellIndex: cellInfo.index ?? null,
        error: String(notebookError),
      });
      return;
    }

    if (!notebookData) {
      return;
    }

    const source = notebookOutputs.length > 0 ? "notebook" : "session";
    const reason = notebookOutputs.length > 0
      ? `matched_by_${matchedNotebookCell?.matchedBy ?? "unknown"}`
      : matchedNotebookCell
        ? "matched_cell_without_outputs"
        : "no_matching_cell_in_notebook";
    const outputTypes = transformedOutputs.map((output: any) => output?.type ?? "unknown");
    const signature = `${source}:${reason}:${cellInfo.id ?? "none"}:${cellInfo.index ?? "none"}:${outputTypes.join(",")}`;

    if (hydrationLogRef.current === signature) {
      return;
    }

    hydrationLogRef.current = signature;
    console.info("[InlineHydration] resolved inline cell outputs", {
      sessionId,
      notebookUrl,
      cellId: cellInfo.id ?? null,
      cellIndex: cellInfo.index ?? null,
      source,
      reason,
      outputCount: transformedOutputs.length,
      outputTypes,
    });
  }, [
    hideContent,
    cellInfo,
    sessionId,
    notebookError,
    notebookData,
    notebookOutputs,
    matchedNotebookCell,
    transformedOutputs,
    notebookUrl,
  ]);

  const [isHovered, setIsHovered] = useState(false);
  const { artifact, setArtifact } = useArtifact();
  const hasDashboard = transformedOutputs.some((output: any) => output?.type === "dashboard");

  // Don't render anything if canceled or no cell info
  if (isCanceled || !cellInfo) {
    return null;
  }

  // Determine title and subtitle for minimal display
  const minimalTitle = type === "edit" ? "Analysis refined" : type === "replace" ? "Analysis updated" : "Financial analysis";
  
  const subtitle = type === "edit" ? "Refined and re-ran analysis" : type === "replace" ? "Rebuilt and re-ran analysis" : "Analysis completed";

  // If hideContent is true (artifact is visible), show minimal tool display
  if (hideContent) {
    const actionText = type === "edit" ? "Refined" : type === "add" ? "Created" : "Updated";
    const displayTitle = cellInfo.purpose || minimalTitle;
    
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="w-full"
      >
        <div 
          className={`rounded-md border ${chrome.card} overflow-hidden`}
          onClick={() => {
            document.querySelector(`[data-cell-index="${cellInfo.index}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
        >
          <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <Code2 className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`} strokeWidth={2} />
              <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                <div className={`text-xs font-medium ${chrome.text} leading-tight line-clamp-1 max-w-[72vw] md:max-w-[28vw]`}>
                  {displayTitle}{" "}
                  <span className={chrome.subtext}>
                    ({actionText})
                  </span>
                </div>
                <span className={`text-[11px] ${chrome.subtext} truncate`}>
                  {subtitle}
                </span>
              </div>
            </div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
              className="shrink-0"
            >
              <CircleCheckBig className="h-4 w-4 text-green-500" />
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }

  // If hideContent is false (artifact is closed), show full NotebookCell as collapsed preview
  const cellPurpose = cellInfo.purpose || minimalTitle;

  // CRITICAL PERF: stabilize the cellData reference across renders.
  //
  // `useChat` re-renders the entire chat tree on every SSE chunk
  // (~10/sec during streaming). Without this useMemo, `cellData` is a
  // brand-new object on every parent render, which defeats the
  // `memo()` wrapper on `NotebookCell` (shallow ref check fails) and
  // cascades into 6 useMemos + 2 useEffects per cell, fanning out
  // across all 9 inline NotebookCell instances. The result was a
  // ~70% sustained main-thread spin and tab footprints in the GBs.
  //
  // Deps below are deliberately granular — `cellInfo` is a prop (stable
  // across renders for a given tool result) but reading specific fields
  // makes the dep list explicit and avoids a stale-ref footgun if the
  // parent ever spreads the object.
  const cellData = useMemo(
    () => ({
      id: `cell-${cellInfo.index || stepIndex}`,
      index: cellInfo.index || stepIndex,
      cell_type: "code" as const,
      purpose: cellPurpose,
      content: cellInfo.content || "",
      outputs: transformedOutputs,
      captured_params: result?.execution_result?.captured_params,
      status: "success" as const,
    }),
    [
      cellInfo.index,
      cellInfo.content,
      stepIndex,
      cellPurpose,
      transformedOutputs,
      result?.execution_result?.captured_params,
    ]
  );

  const handleExpandClick = () => {
    setArtifact((prev) => ({
      ...prev,
      isVisible: true,
      workflowView: hasDashboard ? "dashboard" : "current-run",
    }));
    if (!hasDashboard) {
      setTimeout(() => {
        document.querySelector(`[data-cell-index="${cellInfo.index}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  };

  if (!artifact.hideSteps) {
    return (
      <div className="w-full">
        <NotebookCell
          cell={cellData}
          isMinimal={false}
          forceExpanded={hasDashboard}
          blurContent={blurContent}
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div
        className="relative w-full cursor-pointer"
        style={{ maxHeight: "24rem", overflow: "hidden" }}
        onClick={handleExpandClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <NotebookCell
          cell={cellData}
          isMinimal={false}
          compact
          forceExpanded={hasDashboard}
          blurContent={blurContent}
        />
        <div
          className="absolute inset-0 flex items-center justify-center transition-opacity duration-150"
          style={{ opacity: isHovered ? 1 : 0 }}
        >
          <div className="absolute inset-0 bg-black/40 rounded-lg" />
          <div className="relative flex items-center gap-1.5 rounded-md bg-black/55 backdrop-blur-sm border border-white/15 px-3 py-1.5 text-xs font-medium text-white/90 shadow-sm">
            {hasDashboard ? (
              <>
                <LayoutDashboard className="h-3.5 w-3.5" />
                View Full Analysis
              </>
            ) : (
              <>
                <Maximize2 className="h-3.5 w-3.5" />
                View Details
              </>
            )}
          </div>
        </div>
        {!isHovered && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background to-transparent rounded-b-lg" />
        )}
      </div>
    </div>
  );
}
